import {useMemo} from 'react';
import PropTypes from 'prop-types';

import {Box, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Video as BaseVideo} from '@amblique/base/app/components/shared/video';
import {inflector} from '@amblique/king-living/shared/helpers/inflector';

/**
 * Video component
 *
 * @param {Object} props - Component props
 * @param {Contentful.Video} props.component - Video component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @returns {React.ReactElement}
 */
export const Video = ({component, isComplete = true, isFetching = false, ...props}) => {
  const {videoIdOrUrl, videoAsset, presets, options} = component;

  const styles = useMultiStyleConfig('contentful/video', {component, isComplete, isFetching});

  const normalizedPresets = useMemo(() => {
    return typeof presets === 'string' ? inflector.camelize(presets) : null;
  }, [presets]);

  const {id} = useMemo(() => {
    if (!isComplete && isFetching) {
      return {id: null};
    }

    if (videoIdOrUrl) {
      return {id: videoIdOrUrl};
    }

    if (videoAsset?.url) {
      return {id: `mobile: ${videoAsset.url}`};
    }

    return {id: null};
  }, [videoIdOrUrl, videoAsset, isComplete, isFetching]);

  return (
    (isComplete || !isFetching) && (
      <Box {...styles.root} {...props}>
        <BaseVideo id={id} options={options} presets={normalizedPresets} {...styles.video} />
      </Box>
    )
  );
};

Video.displayName = 'Video';
Video.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
